import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Vault, ArrowLeft, Banknote, ArrowDownToLine, Loader2, TrendingUp,
  AlertTriangle, CheckCircle2, Building2, Plus, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const STATUS_META = {
  pending:           { label: "Pending",            tone: "bg-amber-100 text-amber-800",        Icon: Loader2 },
  posted:            { label: "Posted",             tone: "bg-sky-100 text-sky-700",            Icon: TrendingUp },
  settled:           { label: "Settled",            tone: "bg-emerald-100 text-emerald-700",    Icon: CheckCircle2 },
  funds_available:   { label: "Funds available",    tone: "bg-emerald-100 text-emerald-700",    Icon: CheckCircle2 },
  declined:          { label: "Declined",           tone: "bg-red-100 text-red-700",            Icon: AlertTriangle },
  failed:            { label: "Failed",             tone: "bg-red-100 text-red-700",            Icon: AlertTriangle },
  returned:          { label: "Returned",           tone: "bg-red-100 text-red-700",            Icon: AlertTriangle },
};

function formatDate(s) {
  if (!s) return "";
  try {
    return new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return s;
  }
}

export default function VaultPage() {
  const navigate = useNavigate();
  const [vault, setVault] = useState(null);
  const [plaid, setPlaid] = useState({ configured: false, linked: false, accounts: [], item_count: 0, max_banks: 1 });
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [selectedAccount, setSelectedAccount] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    try {
      const [v, p] = await Promise.all([
        api.get("/vault"),
        api.get("/plaid/status").catch(() => ({ data: { configured: false, linked: false, accounts: [] } })),
      ]);
      setVault(v.data);
      setPlaid(p.data);
      // Default to first depository account
      const dep = (p.data.accounts || []).find((a) => (a.type || "").toLowerCase() === "depository");
      if (dep) setSelectedAccount(`${dep.item_id}::${dep.account_id}`);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't load Vault.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const depositoryAccounts = useMemo(
    () => (plaid.accounts || []).filter((a) => (a.type || "").toLowerCase() === "depository"),
    [plaid.accounts]
  );

  const handleWithdraw = async () => {
    const amt = parseFloat(withdrawAmount);
    if (!amt || amt <= 0) {
      toast.error("Enter an amount.");
      return;
    }
    if (vault && amt > vault.balance) {
      toast.error(`Vault balance is only $${vault.balance.toFixed(2)}.`);
      return;
    }
    if (!selectedAccount) {
      toast.error("Pick a destination account.");
      return;
    }
    const [item_id, account_id] = selectedAccount.split("::");
    setBusy(true);
    try {
      const { data } = await api.post("/vault/withdraw", {
        amount: amt,
        plaid_item_id: item_id,
        plaid_account_id: account_id,
      });
      if (data.status === "declined" || data.status === "failed") {
        toast.error(`Withdraw ${data.status}`, { description: data.message });
      } else {
        toast.success(`Withdraw initiated · ${data.bank_label}`, {
          description: `$${amt.toFixed(2)} · status ${data.status}`,
          duration: 6000,
        });
      }
      setWithdrawOpen(false);
      setWithdrawAmount("");
      await loadAll();
    } catch (e) {
      console.error(e);
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Withdraw failed.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleSimulate = async (transferId, eventType) => {
    try {
      const { data } = await api.post(`/sandbox/transfer/simulate?transfer_id=${transferId}&event_type=${eventType}`);
      toast.success(`Transfer → ${data.status}`);
      await loadAll();
    } catch (e) {
      toast.error("Simulate failed.");
    }
  };

  const balance = vault?.balance || 0;
  const totalDiverted = vault?.total_diverted || 0;
  const recent = vault?.recent_transactions || [];

  return (
    <div className="hero-theme min-h-screen" data-testid="vault-page">
      <div className="max-w-2xl mx-auto w-full pt-8 pb-32 px-5">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900"
          data-testid="vault-back-button"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard
        </button>

        {/* Hero */}
        <div className="mt-4 rounded-3xl p-[2px] bg-gradient-to-br from-amber-400 via-yellow-300 to-amber-500 shadow-lg">
          <div className="rounded-[22px] bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 px-6 py-7 relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-44 h-44 bg-amber-400/30 rounded-full blur-3xl" aria-hidden="true" />
            <div className="absolute -bottom-12 -left-10 w-40 h-40 bg-yellow-300/15 rounded-full blur-3xl" aria-hidden="true" />
            <div className="relative flex items-start gap-3">
              <div className="w-12 h-12 rounded-2xl bg-amber-400 text-slate-900 flex items-center justify-center flex-shrink-0 shadow-md ring-4 ring-amber-400/20">
                <Vault className="w-6 h-6" strokeWidth={2.6} />
              </div>
              <div className="flex-1 min-w-0">
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300 text-[10px] font-black uppercase tracking-[0.18em]">
                  <Sparkles className="w-3 h-3" strokeWidth={3} />
                  Hero Vault
                </span>
                <p className="mt-1.5 text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">Available balance</p>
                <p
                  className="text-4xl font-black text-white tabular-nums leading-none"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                  data-testid="vault-page-balance"
                >
                  ${balance.toFixed(2)}
                </p>
                <p className="mt-1 text-[11px] font-bold text-slate-300">
                  ${totalDiverted.toFixed(2)} diverted in lifetime · {vault?.deposit_count || 0} deposit{(vault?.deposit_count || 0) === 1 ? "" : "s"}
                </p>
              </div>
            </div>
            <div className="relative mt-5 flex items-center gap-2">
              <Button
                type="button"
                onClick={() => setWithdrawOpen(true)}
                disabled={loading || balance <= 0 || !plaid.linked}
                className="h-11 flex-1 rounded-2xl bg-amber-400 hover:bg-amber-300 text-slate-900 font-extrabold gap-2 disabled:opacity-60"
                data-testid="open-withdraw-button"
              >
                <ArrowDownToLine className="w-4 h-4" strokeWidth={2.8} />
                Withdraw to bank
              </Button>
            </div>
            {!plaid.linked && (
              <p className="relative mt-3 text-xs text-amber-200/90 font-semibold">
                Link a bank from your dashboard to enable withdrawals.
              </p>
            )}
          </div>
        </div>

        {/* History */}
        <div className="mt-8" data-testid="vault-history-section">
          <h2
            className="text-2xl font-black text-slate-900"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            History
          </h2>
          <p className="text-xs font-semibold text-slate-500 mt-0.5">Most recent first</p>

          <div className="mt-4 flex flex-col gap-2.5">
            {loading && (
              <div className="rounded-2xl bg-white border-2 border-slate-100 p-5 text-center text-slate-400 font-semibold">
                Loading…
              </div>
            )}
            {!loading && recent.length === 0 && (
              <div className="rounded-2xl bg-white border-2 border-dashed border-slate-200 p-8 text-center" data-testid="vault-history-empty">
                <Banknote className="w-9 h-9 mx-auto text-slate-300" />
                <p className="mt-2 text-sm font-bold text-slate-700">No vault activity yet.</p>
                <p className="text-xs text-slate-500 mt-0.5">Defeat a bill to start funding your vault.</p>
              </div>
            )}
            {!loading && recent.map((t) => {
              const isWithdraw = t.amount < 0;
              const meta = isWithdraw
                ? STATUS_META[t.note?.toLowerCase()?.includes("decline") ? "declined" : "pending"] || STATUS_META.pending
                : null;
              return (
                <div
                  key={t.id}
                  className={`rounded-2xl border-2 p-4 flex items-start gap-3 ${
                    isWithdraw ? "bg-slate-50 border-slate-200" : "bg-white border-emerald-100"
                  }`}
                  data-testid="vault-history-row"
                >
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      isWithdraw ? "bg-slate-900 text-amber-300" : "bg-emerald-500 text-white"
                    }`}
                  >
                    {isWithdraw ? <ArrowDownToLine className="w-4 h-4" strokeWidth={2.8} /> : <TrendingUp className="w-4 h-4" strokeWidth={2.8} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <p
                        className="text-sm font-black text-slate-900 truncate"
                        style={{ fontFamily: "Outfit, sans-serif" }}
                      >
                        {isWithdraw ? "Withdraw" : "Deposit"}
                      </p>
                      <span className={`text-base font-black tabular-nums whitespace-nowrap ${
                        isWithdraw ? "text-slate-700" : "text-emerald-700"
                      }`}>
                        {isWithdraw ? "-" : "+"}${Math.abs(t.amount).toFixed(2)}
                      </span>
                    </div>
                    <p className="text-[12.5px] text-slate-600 font-semibold mt-0.5 truncate">
                      {t.source_name || t.note || "Mocked transfer"}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">
                        {formatDate(t.created_at)}
                      </span>
                      {meta && (
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-[0.1em] ${meta.tone}`}>
                          {meta.label}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Withdraw modal */}
      <Dialog open={withdrawOpen} onOpenChange={(v) => { if (!busy) setWithdrawOpen(v); }}>
        <DialogContent className="sm:max-w-md rounded-3xl border-2 border-slate-100" data-testid="withdraw-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Withdraw to bank
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              ACH payout via Plaid Transfers. In sandbox, transfers stay in <span className="font-bold">pending</span> until simulated.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">Amount (USD)</span>
              <input
                type="number"
                step="0.01"
                min="1"
                max={balance}
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder="0.00"
                className="mt-1 w-full h-12 rounded-xl border-2 border-slate-200 px-3 text-2xl font-black text-slate-900 tabular-nums focus:border-slate-900 focus:outline-none"
                data-testid="withdraw-amount-input"
              />
              <p className="mt-1 text-[11px] font-semibold text-slate-500">
                Available: ${balance.toFixed(2)}
              </p>
            </label>

            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">Destination</span>
              <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                <SelectTrigger className="mt-1 h-11 rounded-xl border-2 border-slate-200 text-sm font-bold" data-testid="withdraw-account-select">
                  <SelectValue placeholder="Choose an account" />
                </SelectTrigger>
                <SelectContent>
                  {depositoryAccounts.length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-500 font-semibold">
                      No depository accounts. Link a checking/savings bank.
                    </div>
                  )}
                  {depositoryAccounts.map((a) => (
                    <SelectItem
                      key={`${a.item_id}::${a.account_id}`}
                      value={`${a.item_id}::${a.account_id}`}
                      data-testid={`withdraw-account-${a.account_id}`}
                    >
                      <span className="inline-flex items-center gap-2 font-bold">
                        <Building2 className="w-3.5 h-3.5 text-slate-500" />
                        {a.institution_name || "Bank"} · {a.name} ••{a.mask || "0000"}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <p className="text-[10px] uppercase tracking-[0.16em] font-black text-slate-400">
              Plaid Transfers · Sandbox
            </p>
          </div>
          <DialogFooter className="flex flex-col-reverse sm:flex-col-reverse gap-2">
            <Button
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={() => setWithdrawOpen(false)}
              className="h-11 rounded-xl text-slate-600 hover:text-slate-900 font-bold"
              data-testid="withdraw-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleWithdraw}
              disabled={busy}
              className="h-12 rounded-xl bg-slate-900 hover:bg-slate-800 text-amber-400 font-black tracking-wide gap-2 disabled:opacity-70"
              data-testid="withdraw-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowDownToLine className="w-4 h-4" strokeWidth={2.8} />}
              Confirm withdraw
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
